import { useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { CalendarSearch, CheckCircle2, LockKeyhole, MoveHorizontal, XCircle } from 'lucide-react'
import type { BookingLookupResult } from '@seaa/shared'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { api, apiErrorMessage } from '../../lib/api'
import { useI18n } from '../../lib/i18n'
import { usePageMetadata } from '../../lib/page-metadata'
import { BookingExperience, BookingHeader } from './booking-shell'
import './manage-flow.css'

export function ManageLookupPage() {
  const { locale, l } = useI18n()
  usePageMetadata({
    title: locale === 'ar' ? 'إدارة حجزك · سِعَة' : 'Manage your booking · Seaa',
    description: locale === 'ar' ? 'افتح حجزك بأمان لعرضه أو تعديله أو إلغائه.' : 'Securely open your private booking to view, reschedule or cancel it.',
    canonicalUrl: `${window.location.origin}/manage`,
    robots: 'noindex,nofollow,noarchive',
    revision: `manage-lookup:${locale}`,
  })
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { code?: string }
  const [bookingCode, setBookingCode] = useState(() => search.code?.trim().toUpperCase() ?? '')
  const [email, setEmail] = useState('')
  const lookup = useMutation({
    mutationFn: () => api<BookingLookupResult>('/api/public/bookings/lookup', {
      method: 'POST',
      body: JSON.stringify({ bookingCode, email }),
    }),
    onSuccess: (result) => void navigate({ to: '/manage/$token', params: { token: result.manageToken }, search: { lang: locale }, replace: true }),
  })
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    lookup.mutate()
  }

  return <BookingExperience className="manage-active">
    <BookingHeader context="manage" />
    <main className="manage-lookup">
      <section className="manage-lookup__intro">
        <div className="manage-active__icon"><CalendarSearch size={25} /></div>
        <h1>{l('Buchung verwalten', 'Manage your booking', 'إدارة حجزك', 'Beheer je boeking')}</h1>
        <p>{l(
          'Finde deine Buchung, um den Termin anzusehen, zu verschieben oder zu stornieren.',
          'Find your booking to view it, reschedule it, or cancel it.',
          'اعثر على حجزك لعرض الموعد أو تعديله أو إلغائه.',
          'Vind je boeking om deze te bekijken, te verplaatsen of te annuleren.',
        )}</p>
      </section>
      <section className="manage-lookup__workbench">
        <form className="manage-lookup__form" onSubmit={submit}>
          <h2>{l('Buchung öffnen','Open booking','فتح الحجز','Boeking openen')}</h2>
          <label className="block text-sm font-semibold">{l('Buchungsnummer', 'Booking number', 'رقم الحجز', 'Boekingsnummer')}
            <Input
              className="mt-2 font-mono uppercase"
              name="bookingCode"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              value={bookingCode}
              onChange={(event) => setBookingCode(event.target.value.toUpperCase())}
              placeholder="SEA-260823-7K4M2-P9Q6X"
              required
            />
          </label>
          <label className="block text-sm font-semibold">{l('E-Mail-Adresse der Buchung', 'Booking email address', 'البريد الإلكتروني المستخدم في الحجز', 'E-mailadres van de boeking')}
            <Input
              className="mt-2"
              name="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          {lookup.isError && <p role="alert" className="manage-active__alert">{apiErrorMessage(lookup.error, locale)}</p>}
          <Button className="w-full" size="lg" type="submit" disabled={lookup.isPending}>
            {lookup.isPending ? l('Buchung wird gesucht …', 'Finding booking…', 'جارٍ البحث عن الحجز…', 'Boeking zoeken…') : l('Buchung öffnen', 'Open booking', 'فتح الحجز', 'Boeking openen')}
          </Button>
          <p className="manage-lookup__privacy"><LockKeyhole size={15} />{l(
          'Wir zeigen eine Buchung nur, wenn Nummer und E-Mail-Adresse übereinstimmen.',
          'We only show a booking when its number and email address match.',
          'لا نعرض الحجز إلا عند تطابق الرقم والبريد الإلكتروني.',
          'We tonen een boeking alleen wanneer nummer en e-mailadres overeenkomen.',
        )}</p>
        </form>
        <aside className="manage-lookup__guide" aria-label={l('Mögliche Aktionen','Available actions','الإجراءات المتاحة','Beschikbare acties')}>
          <div><CheckCircle2 size={20} /><span><strong>{l('Details ansehen','View details','عرض التفاصيل','Details bekijken')}</strong><small>{l('Termin, Ressource und Zahlungsstand.','Time, resource and payment status.','الموعد والخدمة أو المكان وحالة الدفع.','Tijd, middel en betaalstatus.')}</small></span></div>
          <div><MoveHorizontal size={20} /><span><strong>{l('Termin verschieben','Reschedule','تعديل الموعد','Verplaatsen')}</strong><small>{l('Wähle eine neue passende freie Zeit.','Choose another suitable available time.','اختر موعدًا آخر يناسبك من المواعيد المتاحة.','Kies een ander geschikt vrij tijdstip.')}</small></span></div>
          <div><XCircle size={20} /><span><strong>{l('Stornierung prüfen','Check cancellation','مراجعة شروط الإلغاء','Annulering bekijken')}</strong><small>{l('Regeln und Erstattung bleiben sichtbar.','Rules and refund status stay visible.','اطّلع على شروط الإلغاء وحالة استرداد المبلغ.','Regels en terugbetaling blijven zichtbaar.')}</small></span></div>
        </aside>
      </section>
    </main>
  </BookingExperience>
}
